import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  IconButton,
  Tooltip,
  Fab,
  useTheme,
  useMediaQuery,
  Drawer,
  Chip,
  Badge,
  Divider,
  Alert,
  Snackbar,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Avatar,
  Checkbox,
  Menu,
  ListItemIcon,
  ListItemText,
  Pagination,
} from '@mui/material';
import {
  Add,
  Search,
  FilterList,
  Refresh,
  ViewList,
  ViewModule,
  Person,
  Business,
  Phone,
  Email,
  LocationOn,
  Edit,
  Delete,
  Visibility,
  Close,
  Save,
  Cancel,
  CheckCircle,
  Warning,
  Info,
  MoreVert,
  Cake,
} from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { getCustomers, createCustomer, updateCustomer, deleteCustomer } from '../redux/actions/customerActions';

const CustomerMaster = () => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const { customers, pagination, loading, error } = useSelector((state) => state.customers);

  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [formDrawerOpen, setFormDrawerOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    address: '',
    gstin: '',
    date_of_birth: '',
  });
  const [formErrors, setFormErrors] = useState({});
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    dispatch(getCustomers({ page: currentPage, limit: 10, search: searchTerm }));
  }, [dispatch, searchTerm, currentPage]);

  const handleSearch = (term) => {
    setSearchTerm(term);
    setCurrentPage(1); // Reset to first page when searching
  };

  const handlePageChange = (event, page) => {
    setCurrentPage(page);
  };

  const handleAddNew = () => {
    setSelectedCustomer(null);
    setIsEditing(true);
    setFormData({
      name: '',
      phone: '',
      email: '',
      address: '',
      gstin: '',
      date_of_birth: '',
    });
    setFormErrors({});
    if (isMobile) {
      setFormDrawerOpen(true);
    }
  };

  const handleEdit = (customer) => {
    setSelectedCustomer(customer);
    setIsEditing(true);
    setFormData({
      name: customer.name || '',
      phone: customer.phone || '',
      email: customer.email || '',
      address: customer.address || '',
      gstin: customer.gstin || '',
      date_of_birth: customer.date_of_birth || '',
    });
    setFormErrors({});
    if (isMobile) {
      setFormDrawerOpen(true);
    }
  };

  const handleView = (customer) => {
    setSelectedCustomer(customer);
    setIsEditing(false);
    setFormData({
      name: customer.name || '',
      phone: customer.phone || '',
      email: customer.email || '',
      address: customer.address || '',
      gstin: customer.gstin || '',
      date_of_birth: customer.date_of_birth || '',
    });
    if (isMobile) {
      setFormDrawerOpen(true);
    }
  };

  const handleDelete = async (customer) => {
    if (window.confirm(`Are you sure you want to delete ${customer.name}?`)) {
      const result = await dispatch(deleteCustomer(customer.id));
      if (result.success) {
        setSnackbar({
          open: true,
          message: 'Customer deleted successfully',
          severity: 'success'
        });
        dispatch(getCustomers({ page: currentPage, limit: 10, search: searchTerm }));
      } else {
        setSnackbar({
          open: true,
          message: result.message || 'Failed to delete customer',
          severity: 'error'
        });
      }
    }
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    
    // Validate form - only name is required, others are optional
    const errors = {};
    if (!formData.name.trim()) errors.name = 'Name is required';
    if (formData.phone && formData.phone.trim() && !/^[0-9+\-\s()]+$/.test(formData.phone)) {
      errors.phone = 'Invalid phone format';
    }
    if (formData.email && formData.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = 'Invalid email format';
    }
    if (formData.address && formData.address.trim().length < 3) {
      errors.address = 'Address must be at least 3 characters if provided';
    }
    if (formData.gstin && formData.gstin.trim() && !/^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/.test(formData.gstin)) {
      errors.gstin = 'Invalid GSTIN format (e.g., 12ABCDE1234F1Z5)';
    }
    
    setFormErrors(errors);
    if (Object.keys(errors).length > 0) return;

    const result = isEditing && selectedCustomer
      ? await dispatch(updateCustomer(selectedCustomer.id, formData))
      : await dispatch(createCustomer(formData));
    
    if (result.success) {
      setSnackbar({
        open: true,
        message: isEditing ? 'Customer updated successfully' : 'Customer created successfully',
        severity: 'success'
      });
      setFormDrawerOpen(false);
      setSelectedCustomer(null);
      setIsEditing(false);
      dispatch(getCustomers({ page: currentPage, limit: 10, search: searchTerm }));
    } else {
      setSnackbar({
        open: true,
        message: result.message || 'Operation failed',
        severity: 'error'
      });
    }
  };

  const handleFormCancel = () => {
    setFormDrawerOpen(false);
    setSelectedCustomer(null);
    setIsEditing(false);
    setFormData({
      name: '',
      phone: '',
      email: '',
      address: '',
      gstin: '',
      date_of_birth: '',
    });
    setFormErrors({});
  };

  const handleRefresh = () => {
    dispatch(getCustomers({ page: currentPage, limit: 10, search: searchTerm }));
  };

  const getInitials = (name) => {
    return name.split(' ').map(n => n[0]).join('').toUpperCase();
  };

  const getStatusColor = (customer) => {
    if (customer.gstin) return 'success';
    return 'default';
  };

  const getStatusLabel = (customer) => {
    if (customer.gstin) return 'GST Registered';
    return 'Regular';
  };

  const getStats = () => {
    const total = customers.length;
    const gstRegistered = customers.filter(c => c.gstin).length;
    const regular = total - gstRegistered;
    
    return { total, gstRegistered, regular };
  };

  const stats = getStats();

  return (
    <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column', p: 2, gap: 2 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box>
          <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold', color: 'primary.main' }}>
            Customer Master
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage your customer database with advanced features
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="Refresh">
            <IconButton onClick={handleRefresh} disabled={loading}>
              <Refresh />
            </IconButton>
          </Tooltip>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={handleAddNew}
            sx={{ borderRadius: 2 }}
          >
            Add Customer
          </Button>
        </Box>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={4}>
          <Card sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="h4" fontWeight="bold">
                    {stats.total}
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    Total Customers
                  </Typography>
                </Box>
                <Person sx={{ fontSize: 40, opacity: 0.8 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card sx={{ background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)', color: 'white' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="h4" fontWeight="bold">
                    {stats.gstRegistered}
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    GST Registered
                  </Typography>
                </Box>
                <Business sx={{ fontSize: 40, opacity: 0.8 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card sx={{ background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)', color: 'white' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="h4" fontWeight="bold">
                    {stats.regular}
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    Regular Customers
                  </Typography>
                </Box>
                <Person sx={{ fontSize: 40, opacity: 0.8 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Grid container spacing={2} sx={{ flex: 1, minHeight: 0 }}>
        {/* Left Side - Customer Grid */}
        <Grid item xs={12} md={7}>
          <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column', p: 0 }}>
              {/* Search Bar */}
              <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2 }}>
                  <TextField
                    fullWidth
                    placeholder="Search customers..."
                    value={searchTerm}
                    onChange={(e) => handleSearch(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Search color="action" />
                        </InputAdornment>
                      ),
                    }}
                    sx={{ maxWidth: 400 }}
                  />
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="body2" color="text.secondary">
                    {pagination.totalItems} customers found
                  </Typography>
                  <Chip
                    label={`Page ${pagination.currentPage} of ${pagination.totalPages}`}
                    size="small"
                    variant="outlined"
                  />
                </Box>
              </Box>

              {/* Customer List */}
              <Box sx={{ flex: 1, overflow: 'auto' }}>
                {loading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200 }}>
                    <Typography>Loading...</Typography>
                  </Box>
                ) : customers.length === 0 ? (
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: 200, p: 3 }}>
                    <Person sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                    <Typography variant="h6" color="text.secondary" gutterBottom>
                      No customers found
                    </Typography>
                    <Typography variant="body2" color="text.secondary" align="center">
                      {searchTerm ? 'Try adjusting your search terms' : 'Add your first customer to get started'}
                    </Typography>
                  </Box>
                ) : (
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow sx={{ backgroundColor: 'grey.50' }}>
                          <TableCell>Customer</TableCell>
                          <TableCell>Contact</TableCell>
                          <TableCell>Status</TableCell>
                          <TableCell align="center">Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {customers.map((customer) => (
                          <TableRow 
                            key={customer.id}
                            hover
                            sx={{ 
                              cursor: 'pointer',
                              '&:hover': { backgroundColor: 'action.hover' }
                            }}
                            onClick={() => handleView(customer)}
                          >
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <Avatar sx={{ bgcolor: 'primary.main', width: 40, height: 40 }}>
                                  {getInitials(customer.name)}
                                </Avatar>
                                <Box>
                                  <Typography variant="subtitle2" fontWeight="bold">
                                    {customer.name}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    ID: {customer.id}
                                  </Typography>
                                </Box>
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Box>
                                {customer.phone && (
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                                    <Phone sx={{ fontSize: 14, color: 'text.secondary' }} />
                                    <Typography variant="body2">{customer.phone}</Typography>
                                  </Box>
                                )}
                                {customer.email && (
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <Email sx={{ fontSize: 14, color: 'text.secondary' }} />
                                    <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                                      {customer.email}
                                    </Typography>
                                  </Box>
                                )}
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={getStatusLabel(customer)}
                                size="small"
                                color={getStatusColor(customer)}
                                variant="outlined"
                              />
                            </TableCell>
                            <TableCell align="center">
                              <Box sx={{ display: 'flex', gap: 0.5 }}>
                                <Tooltip title="Edit">
                                  <IconButton
                                    size="small"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleEdit(customer);
                                    }}
                                    color="primary"
                                  >
                                    <Edit fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Delete">
                                  <IconButton
                                    size="small"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleDelete(customer);
                                    }}
                                    color="error"
                                  >
                                    <Delete fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </Box>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Box>

              {/* Pagination */}
              {!loading && customers.length > 0 && pagination.totalPages > 1 && (
                <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider', display: 'flex', justifyContent: 'center' }}>
                  <Pagination
                    count={pagination.totalPages}
                    page={currentPage}
                    onChange={handlePageChange}
                    color="primary"
                    showFirstButton
                    showLastButton
                    size="small"
                  />
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Right Side - Customer Form */}
        <Grid item xs={12} md={5}>
          <Card sx={{ height: '100%' }}>
            <CardContent sx={{ height: '100%', display: 'flex', flexDirection: 'column', p: 0 }}>
              {/* Form Header */}
              <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box>
                    <Typography variant="h6" gutterBottom>
                      {isEditing ? 'Edit Customer' : selectedCustomer ? 'Customer Details' : 'Add New Customer'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {isEditing ? 'Update customer information' : selectedCustomer ? 'View customer details' : 'Enter customer information'}
                    </Typography>
                  </Box>
                  {selectedCustomer && (
                    <IconButton onClick={handleFormCancel} size="small">
                      <Close />
                    </IconButton>
                  )}
                </Box>
              </Box>

              {/* Form Content */}
              <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
                {error && (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                  </Alert>
                )}

                <form onSubmit={handleFormSubmit}>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Customer Name"
                        name="name"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        error={!!formErrors.name}
                        helperText={formErrors.name}
                        disabled={!isEditing && selectedCustomer}
                        required
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Person color="action" />
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Phone Number"
                        name="phone"
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        error={!!formErrors.phone}
                        helperText={formErrors.phone}
                        disabled={!isEditing && selectedCustomer}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Phone color="action" />
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Email Address"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        error={!!formErrors.email}
                        helperText={formErrors.email || 'Optional'}
                        disabled={!isEditing && selectedCustomer}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Email color="action" />
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Date of Birth"
                        name="date_of_birth"
                        type="date"
                        value={formData.date_of_birth || ''}
                        onChange={(e) => setFormData({ ...formData, date_of_birth: e.target.value })}
                        disabled={!isEditing && selectedCustomer}
                        InputLabelProps={{
                          shrink: true,
                        }}
                        helperText="Optional - Used for sending birthday wishes"
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Cake color="action" />
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="GSTIN"
                        name="gstin"
                        value={formData.gstin}
                        onChange={(e) => setFormData({ ...formData, gstin: e.target.value.toUpperCase() })}
                        error={!!formErrors.gstin}
                        helperText={formErrors.gstin || 'Optional - Format: 12ABCDE1234F1Z5'}
                        disabled={!isEditing && selectedCustomer}
                        placeholder="12ABCDE1234F1Z5"
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Business color="action" />
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Address"
                        name="address"
                        value={formData.address}
                        onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                        error={!!formErrors.address}
                        helperText={formErrors.address}
                        disabled={!isEditing && selectedCustomer}
                        multiline
                        rows={3}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start" sx={{ alignSelf: 'flex-start', mt: 1 }}>
                              <LocationOn color="action" />
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>
                  </Grid>
                </form>
              </Box>

              {/* Form Actions */}
              {isEditing && (
                <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider' }}>
                  <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                    <Button
                      variant="outlined"
                      startIcon={<Cancel />}
                      onClick={handleFormCancel}
                    >
                      Cancel
                    </Button>
                    <Button
                      variant="contained"
                      startIcon={<Save />}
                      onClick={handleFormSubmit}
                    >
                      {selectedCustomer ? 'Update' : 'Create'}
                    </Button>
                  </Box>
                </Box>
              )}

              {!isEditing && selectedCustomer && (
                <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider' }}>
                  <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                    <Button
                      variant="outlined"
                      startIcon={<Edit />}
                      onClick={() => setIsEditing(true)}
                    >
                      Edit
                    </Button>
                    <Button
                      variant="outlined"
                      color="error"
                      startIcon={<Delete />}
                      onClick={() => handleDelete(selectedCustomer)}
                    >
                      Delete
                    </Button>
                  </Box>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Mobile Form Drawer */}
      <Drawer
        anchor="bottom"
        open={formDrawerOpen && isMobile}
        onClose={handleFormCancel}
        sx={{
          '& .MuiDrawer-paper': {
            height: '80vh',
            borderTopLeftRadius: 16,
            borderTopRightRadius: 16,
          },
        }}
      >
        <Box sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">
              {isEditing ? 'Edit Customer' : selectedCustomer ? 'Customer Details' : 'Add New Customer'}
            </Typography>
            <IconButton onClick={handleFormCancel}>
              <Close />
            </IconButton>
          </Box>
          {/* Mobile form content would go here */}
        </Box>
      </Drawer>

      {/* Floating Action Button for Mobile */}
      {isMobile && (
        <Fab
          color="primary"
          aria-label="add"
          onClick={handleAddNew}
          sx={{
            position: 'fixed',
            bottom: 16,
            right: 16,
            zIndex: 1000,
          }}
        >
          <Add />
        </Fab>
      )}

      {/* Snackbar for Notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CustomerMaster;